<script lang="ts">
  import { preventDefault } from "svelte/legacy";

  import type { StrawberryContract } from "$lib/types/contracts";
  import { contentBlockComponents } from "$lib/portableText/utils";
  import { breakpoints as br } from "$lib/utils/getBreakpoints";
  import { syncSessionValues } from "$lib/sessionValues";
  import { PortableText } from "@portabletext/svelte";
  import MetaData from "$lib/MetaData.svelte";
  import Cover from "$lib/Cover.svelte";
  import SanityPicture from "$lib/SanityPicture.svelte";
  import Picture from "$lib/Picture.svelte";
  import Lead from "$lib/Lead.svelte";
  import TypeformButton from "$lib/TypeformButton.svelte";

  type Kunde = {
    fornavn?: string;
    etternavn?: string;
    adresse_1?: string;
    adresse_2?: string;
    postnr?: string;
    poststed?: string;
    mobilnr?: string;
    epost?: string;
    fodselsdato?: string;
  };
  type MembershipResult = {
    ok: boolean;
    message?: string;
    kunde?: Kunde;
  };

  const checkMembership = async () => {
    if (memberNumber === "" || mobileNumber === "") {
      missingInfo = true;
    } else {
      const result = await fetch(
        `https://dokumenter.servicekanal.no/choiceclub/validate/${memberNumber}?sectoken=7bc37f8d-dcfa-4201-9213-70d6c9c3db4f&email=&mobile=%2B47${mobileNumber}&_=1680007405382`
      );
      const membership_res: MembershipResult = await result.json();

      if (membership_res?.ok) {
        membershipFound = true;
        kunde = membership_res.kunde;

        syncSessionValues(
          { mnr: memberNumber, mnr_type: "strawberry" },
          { useBrowserStorage: true }
        );
      } else {
        membershipFound = false;
      }

      missingInfo = false;
    }
  };

  interface Props {
    data: {
      strawberry_contract: Readonly<StrawberryContract>;
    };
  }

  let { data }: Props = $props();

  let memberNumber = $state("");
  let mobileNumber = $state("");
  let submitted = $state(false);
  let membershipFound = $state(false);
  let missingInfo = $state(true);
  let kunde: Kunde | undefined = $state();
  let strawberry = $derived(data?.strawberry_contract);
  let contract = $derived(strawberry?.contract);
  let customParameters = $derived({
    produkt: "Spotpris i Strawberry",
    ...(!!kunde?.fornavn && !!kunde?.etternavn && { navn: `${kunde.fornavn} ${kunde.etternavn}` }),
    ...(!!(kunde?.adresse_1 || kunde?.adresse_2) && {
      postadresse: `${kunde.adresse_1 || ""} ${kunde.adresse_2 || ""}`,
    }),
    ...(!!kunde?.postnr && { postnummer: kunde.postnr }),
    ...(!!kunde?.poststed && { poststed: kunde.poststed }),
    ...(!!kunde?.mobilnr && { mobil: kunde.mobilnr }),
    ...(!!kunde?.epost && { e_post: kunde.epost }),
    ...(!!kunde?.fodselsdato && { fdato: kunde.fodselsdato }),
    product_id: contract.product_id || "",
  });

  // NOTE: for denne løypa:
  // Robin må peke denne i Podio så input håndteres;
  // Morten må verifisere at data vi får tilbake er riktig - hvordan blir det for nye kunder? Ser ut som kundedata kommer fra internt?
  let typeformUrl = $derived(
    typeof kunde !== "undefined" && kunde !== null
      ? `https://ishavskraft.typeform.com/to/GryuQpmj`
      : `https://form.typeform.com/to/euUF2JJe`
  );
</script>

<MetaData
  data={{
    title: `${contract.name} – ${strawberry.hero}`,
    description: contract.description,
  }}
/>

<Cover
  class="mt-2 mb-8"
  --cover-img-lqip={`url("${contract.picture?.lqip}")`}
  pictureFallbackBackground
>
  <div
    class="pt-[calc(5*var(--text-h-hero))] pb-8 laptop:pr-[calc(12.5vw-var(--w-cover-x))] text-white"
  >
    <h1 class="my-0 text-h-hero font-title tablet:leading-snug">
      {strawberry.hero}
    </h1>
  </div>

  {#snippet background()}
    <div>
      {#if contract.picture?.src}
        <SanityPicture
          pictureObject={contract.picture}
          sizes={`(min-width: ${br.desktop}) 85vw, (min-width: ${br.laptop}) 90vw, 100vw`}
          class="h-full w-full object-cover laptop:rounded-2xl"
          loading="eager"
        />
      {/if}
    </div>
  {/snippet}
</Cover>

<div class="mx-edge-x">
  <div class="children-max-w-prose [--space-block:var(--s-6)] laptop:ml-cover-x">
    <h2 class="mb-0">{contract.name}</h2>
    <Lead class="max-w-prose">{contract.description}</Lead>

    <div class="text-body">
      <PortableText
        value={contract.content}
        components={contentBlockComponents}
        onMissingComponent={false}
      />
    </div>

    <hr />
    <p class="text-body">
      <strong class="block">Legg inn ditt medlems- og mobilnummer nedenfor</strong>
      – da vil du kunne lese mer om varmepumpene vi tilbyr, eller bestille strømavtale.
    </p>
  </div>

  <div class="flex justify-center flex-col">
    <form onsubmit={preventDefault(checkMembership)} id="strawberry-form" class:submitted>
      <div class="responsive-wrapper laptop:mx-cover-x">
        <p class="flex flex-col text-body w-full md:w-1/2">
          <label for="mnr"
            ><strong class="block mb-2"> Ditt medlemsnr. hos Strawberry (kun siffer)</strong></label
          >
          <input
            bind:value={memberNumber}
            id="mnr"
            name="mnr"
            type="text"
            pattern="^[0-9]*$"
            required
            placeholder="Medlemsnummer..."
          />
        </p>
        <p class="flex flex-col text-body w-full md:w-1/2">
          <label for="mobil"><strong class="block mb-2">Ditt mobilnummer (8 siffer)</strong></label>
          <input
            bind:value={mobileNumber}
            id="mobil"
            name="mobil"
            type="text"
            minlength="8"
            maxlength="8"
            required
            placeholder="Mobilnummer..."
          />
        </p>
      </div>
      <div class="text-center my-4 text-body">
        <p>
          <button onclick={() => (submitted = true)} class="button button-primary"
            >Sjekk medlemskap for å bestille</button
          >
        </p>
        {#if missingInfo}
          <p class="info">Vi trenger både medlemsnummer og mobilnummer for å sjekke medlemskap</p>
        {/if}
        {#if submitted && !missingInfo && !membershipFound}
          <p class="info">
            Vi fant deg dessverre ikke hos Strawberry. Husket du å bruke det mobilnummeret som er
            registrert for ditt medlemsnummer? Prøv gjerne på nytt, eller ta kontakt med oss i
            Ishavskraft.
          </p>
        {/if}
        {#if submitted && !missingInfo && membershipFound && kunde !== null}
          <p class="info">
            Medlemskap funnet{kunde?.fornavn ? ` for ${kunde.fornavn}` : ""}! Bestill nedenfor.
          </p>
        {:else if submitted && !missingInfo && membershipFound && kunde === null}
          <p class="info">Medlemskap funnet! Bestill nedenfor.</p>
        {/if}
      </div>
    </form>
  </div>

  <div class="responsive-wrapper blue-box">
    {#if strawberry.spotpris_img?.src}
      <Picture
        src={strawberry.spotpris_img.src}
        alt={strawberry.spotpris_img.alt}
        srcset={strawberry.spotpris_img.srcset}
        srcsetWebp={strawberry.spotpris_img.srcsetWebp}
        class="object-cover w-full tablet:w-35% tablet:h-initial"
        --picture-aspect-ratio="16/9"
      />
    {/if}
    <div class="px-4 my-6 flex-1">
      <h2>{strawberry.spotpris_title ?? "Spotpris i Strawberry"}</h2>
      <PortableText
        value={strawberry.spotpris}
        components={contentBlockComponents}
        onMissingComponent={false}
      />
      <div class="mt-6">
        {#if membershipFound}
          <p>
            <TypeformButton class="button-primary-success" url={typeformUrl} {customParameters}>
              Bestill strømavtale
            </TypeformButton>
          </p>
        {:else}
          <p>
            <button class="button button-primary" disabled>Bestill strømavtale</button>
          </p>
        {/if}
      </div>
    </div>
  </div>

  <div class="responsive-wrapper blue-box">
    {#if strawberry.varmepumpe_img?.src}
      <Picture
        src={strawberry.varmepumpe_img.src}
        alt={strawberry.varmepumpe_img.alt}
        srcset={strawberry.varmepumpe_img.srcset}
        srcsetWebp={strawberry.varmepumpe_img.srcsetWebp}
        class="object-cover w-full tablet:w-35% tablet:h-initial"
        --picture-aspect-ratio="16/9"
      />
    {/if}
    <div class="px-4 my-6 flex-1">
      <h2>{strawberry.varmepumpe_title ?? "Varmepumpe i Strawberry"}</h2>
      <PortableText
        value={strawberry.varmepumpe}
        components={contentBlockComponents}
        onMissingComponent={false}
      />
      <div class="mt-6">
        {#if membershipFound}
          <p>
            <a
              class="button button-primary-success"
              href="/nettbutikk/kategori/varmepumpe"
              rel="nofollow">Bestill og få 10 000 Spenn</a
            >
          </p>
        {:else}
          <p>
            <button class="button button-primary" disabled>Bestill og få 10 000 Spenn</button>
          </p>
        {/if}
      </div>
    </div>
  </div>

  <div class="my-3 children-my-1 text-end pl-inline text-base">
    <PortableText
      value={contract.finePrint}
      components={contentBlockComponents}
      onMissingComponent={false}
    />
  </div>
  {#if contract.documents && contract.documents.length > 0}
    <ul class="my-8">
      {#each contract.documents as doc (doc)}
        <li>
          <a href={doc.url} target="_blank">{doc.title ?? "Fil uten navn"}</a>
        </li>
      {/each}
    </ul>
  {/if}
</div>

<style>
  input {
    font-size: var(--text-body);
    border: 1px solid;
    border-radius: 1rem;
    padding: 1rem;
  }

  input:hover,
  input:active,
  input:focus {
    background-color: var(--primary-light-40);
    --border-button: var(--primary-strong);
  }

  input:active,
  input:focus {
    border-color: var(--focus);
    outline: var(--outline);
  }

  .submitted input:invalid {
    border: 1px solid var(--danger);
  }

  .submitted input:focus:invalid {
    border-color: var(--focus);
  }

  .info {
    font-weight: 600;
    margin-block-end: var(--s-8);
  }

  .blue-box {
    background-color: var(--primary-white);
    border-radius: 1rem;
    margin-block: var(--s-8);
  }

  .blue-box :global(img) {
    border-radius: 1rem 1rem 0 0;
  }

  .responsive-wrapper {
    display: flex;
    flex-direction: column;
  }

  @media (--tablet) {
    .blue-box {
      padding-inline-end: 2vw;
    }
    .blue-box :global(img) {
      border-radius: 1rem 0 0 1rem;
    }
    .responsive-wrapper {
      flex-direction: row;
      gap: 2rem;
    }
  }
</style>
